import React, { useState, useContext, useEffect } from "react";
import styles from "./MySolutions.module.scss";
import {
  HourglassEmptyRounded,
  DoneRounded,
  FavoriteRounded,
} from "@material-ui/icons";
import AppContext from "context/AppContext";
import apiCalls from "shared/utils/apiCalls";
import SolutionsList from "shared/components/SolutionsList/SolutionsList";
import CardChallenge from "shared/components/CardChallenge/CardChallenge";
import Grid from "@material-ui/core/Grid";
import CardChallengeSkeleton from "shared/components/CardChallenge/CardChallengeSkeleton/CardChallengeSkeleton";
import CardSolutionSkeleton from "shared/components/CardSolution/CardSolutionSkeleton/CardSolutionSkeleton";
import Tabs from "shared/components/Tabs/Tabs";

enum TabValues {
  inProgress = "in-progress",
  completed = "completed",
  liked = "liked",
}

const tabs = [
  {
    value: TabValues.inProgress,
    label: "In Progress",
    Icon: HourglassEmptyRounded,
  },
  { value: TabValues.completed, label: "Completed", Icon: DoneRounded },
  { value: TabValues.liked, label: "Liked", Icon: FavoriteRounded },
];

const MySolutions = () => {
  const [tab, setTab] = useState<TabValues>(TabValues.inProgress);

  const { state } = useContext(AppContext);
  const [completedSolutions, setCompletedSolutions] = useState<any>();
  const [activeSolutions, setActiveSolutions] = useState<any>();
  const [favoriteSolutions, setFavoriteSolutions] = useState<any>();

  const getSolution = async (filter: "inProgress" | "submitted") => {
    return await apiCalls({
      method: "get",
      url: `/solutions/user/${state.user.uid}?filter=${filter}`,
    });
  };

  const getLikedSolution = async () => {
    const res = await apiCalls({
      method: "get",
      url: "/favorite-solutions",
    });

    if (res) {
      setFavoriteSolutions(res.data);
    }
  };

  useEffect(() => {
    if (!state.user) {
      return;
    }

    const getSolutions = async () => {
      const activeSolutionRes = await getSolution("inProgress");

      if (activeSolutionRes) {
        setActiveSolutions(activeSolutionRes.data);
      }

      const completedSolutionRes = await getSolution("submitted");

      if (completedSolutionRes) {
        setCompletedSolutions(completedSolutionRes.data);
      }
    };

    getSolutions();

    getLikedSolution();

    // eslint-disable-next-line
  }, [state.user]);

  const findChallenge = (challengeId: string) => {
    if (!state.challenges) {
      return;
    }

    return state.challenges.find(({ id }: any) => challengeId === id);
  };

  if (!state.user) {
    return (
      <div className={styles.mySolutions_noResult}>
        <p>You have to login to see this page!</p>
      </div>
    );
  }

  return (
    <div className={styles.mySolutions}>
      <Tabs currentTabValue={tab} tabs={tabs} onTabSelected={setTab} />

      {tab === TabValues.inProgress && (
        <>
          {activeSolutions ? (
            <>
              {activeSolutions.length > 0 ? (
                <Grid container direction="row" spacing={3}>
                  {activeSolutions.map(({ challengeId }: any) => (
                    <>
                      {challengeId && (
                        <Grid item xs={12} md={6} lg={4} key={challengeId}>
                          <CardChallenge
                            challenge={findChallenge(challengeId)}
                            to={`/challenges/${challengeId}`}
                          />
                        </Grid>
                      )}
                    </>
                  ))}
                </Grid>
              ) : (
                <div className={styles.mySolutions_noResult}>
                  <h3>No projects found</h3>
                </div>
              )}
            </>
          ) : (
            <Grid container direction="row" spacing={3}>
              {[0, 1, 2, 3, 4, 5].map((number: number) => (
                <Grid item xs={12} md={6} lg={4} key={number}>
                  <CardChallengeSkeleton />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {tab === TabValues.completed && (
        <>
          {completedSolutions ? (
            <>
              {completedSolutions.length > 0 ? (
                <SolutionsList solutions={completedSolutions} />
              ) : (
                <div className={styles.mySolutions_noResult}>
                  <h3>No projects found</h3>
                </div>
              )}
            </>
          ) : (
            <Grid container direction="row" spacing={3}>
              {[0, 1, 2, 3, 4, 5].map((number) => (
                <Grid item xs={12} md={6} key={number}>
                  <CardSolutionSkeleton key={number} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {tab === TabValues.liked && (
        <>
          {favoriteSolutions ? (
            <>
              {favoriteSolutions.length > 0 ? (
                <SolutionsList solutions={favoriteSolutions} />
              ) : (
                <div className={styles.mySolutions_noResult}>
                  <h3>No projects found</h3>
                </div>
              )}
            </>
          ) : (
            <Grid container direction="row" spacing={3}>
              {[0, 1, 2, 3, 4, 5].map((number) => (
                <Grid item xs={12} md={6} key={number}>
                  <CardSolutionSkeleton key={number} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </div>
  );
};

export default MySolutions;
